import { TagGeneratingFilesContext, TagGeneratingFilesContextValue } from '@trinity/chat-components/containers/Workspace/context.js';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Props = {
  children: React.ReactNode;
};

const STORAGE_KEY = 'tag-generating-files';
const STORAGE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

type StoredData = {
  fileIds: number[];
  timestamp: number;
};

// Load from localStorage on mount
function loadFromStorage(): Set<number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Set();

    const data: StoredData = JSON.parse(stored);
    const now = Date.now();

    // Remove expired entries
    if (now - data.timestamp > STORAGE_EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return new Set();
    }

    return new Set(data.fileIds);
  } catch {
    return new Set();
  }
}

// Save to localStorage
function saveToStorage(fileIds: Set<number>) {
  try {
    if (fileIds.size === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const data: StoredData = {
      fileIds: Array.from(fileIds),
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silently handle localStorage errors
  }
}

export const TagGeneratingFilesProvider = ({ children }: Props) => {
  // Initialize from localStorage on mount
  const [generatingFileIds, setGeneratingFileIds] = useState<Set<number>>(() => loadFromStorage());

  // Sync to localStorage whenever state changes
  useEffect(() => {
    saveToStorage(generatingFileIds);
  }, [generatingFileIds]);

  const addGeneratingFiles = useCallback((fileIds: number[]) => {
    setGeneratingFileIds(prev => {
      const next = new Set(prev);
      fileIds.forEach(fileId => {
        next.add(fileId);
      });
      return next;
    });
  }, []);

  const removeGeneratingFile = useCallback((fileId: number) => {
    setGeneratingFileIds(prev => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
  }, []);

  const isGenerating = useCallback((fileId: number) => {
    return generatingFileIds.has(fileId);
  }, [generatingFileIds]);

  const value = useMemo<TagGeneratingFilesContextValue>(() => ({
    generatingFileIds,
    addGeneratingFiles,
    removeGeneratingFile,
    isGenerating,
  }), [generatingFileIds, addGeneratingFiles, removeGeneratingFile, isGenerating]);

  return (
    <TagGeneratingFilesContext.Provider value={value}>
      {children}
    </TagGeneratingFilesContext.Provider>
  );
};